import {
  Button,
  Flex,
  Image,
  Spinner,
  Text,
} from '@chakra-ui/react'

import type { Quest } from '@olagg/db-types'
import { QUEST_STATUS, QUEST_TYPES } from '@olagg/db-types'
import { useAuthStore, useOnboardingStore } from '@olagg/store'
import { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { analytics } from '../../../analytics'
import questStyles from "@olagg/ui-kit/QuestCard/styles.module.css"
import useQuest from '../hooks/useQuest'
import slugify from '../../../utils/slugify'
import ModalSecretWord from './ModalSecretWord'
import ModalTwitterQuest from './ModalTwitterQuest'
import { cdn } from '@/utils/cloudflareCdn'
import { AdTrackingContext } from '@/contexts/adTrackingContext'
import { useTranslation } from 'react-i18next'
import { ThirdwebProvider, en, es, metamaskWallet, walletConnect } from '@thirdweb-dev/react'
import { ThirdwebProvider as ThirdwebProviderV5 } from 'thirdweb/react'

import XpGradient from '../assets/xp.svg'
import IconLocked from '../assets/icon_locked.svg'
import IconCheck from '../assets/icon_check.svg'
import IconClock from '../assets/icon_clock.svg'
import CountdownToTomorrow from './Countdown'
import BgPartnerIcon from '../assets/bg_partner_icon.svg'
import QuestWeb3Button from './QuestWeb3Button'
import XpPill from '@/components/XpPill'

type QuestCardProps = {
  quest: Quest
  reloadQuests: () => Promise<void>
  width?: string,
  loading?: 'eager' | 'lazy',
}

const QuestCard = ({
  quest,
  reloadQuests,
  width,
  loading = 'lazy',
}: QuestCardProps) => {
  const { fcb, fbp } = useContext(AdTrackingContext)
  const { t } = useTranslation();
  const lang = localStorage.getItem('i18n_lang') || 'es'
  const [hover, setHover] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const { loggedIn, getMe } = useAuthStore()
  const { openOnboarding } = useOnboardingStore()
  const navigate = useNavigate()
  const { completeQuest, claimQuest } = useQuest()
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [isOpenModalTwitter, setIsOpenModalTwitter] = useState(false)

  const claimed = quest.status === QUEST_STATUS.CLAIMED
  const completed = quest.status === QUEST_STATUS.COMPLETED
  const disabled = quest.status === QUEST_STATUS.DISABLED
  const pending = quest.status === QUEST_STATUS.PENDING
  const exhausted = quest.status === QUEST_STATUS.EXHAUSTED
  const verifyingWeb3 = quest.status === QUEST_STATUS.VERIFYING

  const adminCompletion = quest.type === QUEST_TYPES.ADMIN_COMPLETION
  const automatic = quest.type === QUEST_TYPES.AUTOMATIC
  const manual = quest.type === QUEST_TYPES.MANUAL
  const oneClick = quest.type === QUEST_TYPES.ONE_CLICK
  const secretWord = quest.type === QUEST_TYPES.SECRET_WORD
  const twitter = quest.type === QUEST_TYPES.TWITTER
  const description = lang === 'es' ? quest.description : quest.extraData?.[lang]?.description || quest.description
  const buttonText = lang === 'es' ? quest.buttonText : quest.extraData?.[lang]?.buttonText || quest.buttonText
  const parent = quest.parents?.[0]
  const isPartner = quest?.owner?.isPartner

  const dependsOnFilteredStatus = parent && quest.status !== QUEST_STATUS.PENDING || false

  const title = () => {
    const _title = lang === 'es' ? quest.title : quest.extraData?.[lang]?.title || quest.title
    if (quest.recursive) {
      const n = quest.iteration * quest.multiplier
      return _title?.replace('[N]', n.toString())
    }
    return _title
  }

  const onMouseEnterHandler = () => {
    if ((quest.description || dependsOnFilteredStatus) && !claimed && !exhausted) setHover(true)
  }

  const onMouseLeaveHandler = () => setHover(false)

  const goToUrl = () => {
    if (!loggedIn) return openOnboarding()
    if (quest.action.startsWith('https://')) window.open(quest.action, '_blank')
    else navigate(quest.action)
  }

  const handleCompleteQuest = async () => {

    if (!loggedIn) return openOnboarding()
    if (secretWord) return handleModalSecretWord()
    if (twitter) return setIsOpenModalTwitter(true)

    try {
      setVerifying(true)
      const props = {
        quest_id: quest.id,
        quest_name: quest.title,
        quest_xp: quest.score,
        user_agent: navigator.userAgent,
        ...(fcb && { fbc: fcb }),
        ...(fbp && { fbp: fbp }),
      }

      if (oneClick) {
        analytics()
          ?.track('Quest Started', props)
      }

      await completeQuest(quest, { location: window.location.href })

      analytics().track('Quest Verification Requested', props)

      setVerifying(false)
      await reloadQuests()
    } catch (error) {
      setVerifying(false)
    }
  }

  const handleClaim = async () => {
    if (!loggedIn) return openOnboarding()
    setVerifying(true)
    await claimQuest(quest)
    setVerifying(false)
    await reloadQuests()
    await getMe()
  }

  const onSubmitSecretWord = async () => {
    await reloadQuests()
    await getMe()
  }

  const handleModalSecretWord = () => {
    const props = {
      quest_id: quest.id,
      quest_name: quest.title,
      quest_xp: quest.score,
      user_agent: navigator.userAgent,
      ...(fcb && { fbc: fcb }),
      ...(fbp && { fbp: fbp }),
    }
    analytics()
      ?.track('Quest Started', props)
    setIsOpenModal(true)
  }

  const scrollToDependsOn = (parent) => {
    const card = document.getElementById(slugify(parent.title))
    if (card !== null) {
      const y = card.getBoundingClientRect().top + window.scrollY - 120;
      window.scroll({
        top: y,
        behavior: 'smooth'
      });
    }
  }

  // Quest Web3
  const blockchainNeeded = quest?.blockchainNeeded

  return (
    <>
      <Flex
        id={slugify(quest.title)}
        bg="white"
        rounded="12px"
        width={{ base: '100%', md: '335px', xl: '285px' }}
        minWidth={{ base: '100%', md: '335px', xl: '285px' }}
        maxWidth={'100%'}
        // minHeight="380px"
        position="relative"
        flexDirection="column"
        onMouseEnter={onMouseEnterHandler}
        onMouseLeave={onMouseLeaveHandler}
        overflow="hidden"
      >

        {/* Header */}
        <Flex w={'full'} height='140px' position='relative'>
          {/* Image */}
          <Flex position={'absolute'} w={'full'} background={'linear-gradient(45deg, #F8AC33 0%, #912FD4 130%)'} height='100%' zIndex={0} />
          {quest.banner ? (
            <Image loading={loading}
              objectFit='cover'
              w='100%'
              height="100%"
              backgroundPosition="center center"
              src={cdn(quest.banner, { w: '320px' })}
              zIndex={1}
            />
          ) : (
            <>
              <Flex
                w='100%'
                height="100%"
                zIndex={1}
              >
                <Image loading={loading}
                  objectFit='cover'
                  w='100%'
                  backgroundPosition="center center"
                  src={"/pattern_md.svg"}
                  opacity='0.4'
                />
              </Flex>
            </>
          )}
          {/* Icono */}
          <Flex
            position="absolute"
            justifyContent={'center'}
            alignItems={'center'}
            bottom="-36px"
            left="16px"
            bgColor={'#3F2091'}
            w='72px'
            h='72px'
            border='2px solid white'
            borderRadius={'50%'}
            zIndex={2}
          >
            {isPartner ? (
              <Flex position={'relative'}>
                <Image src={BgPartnerIcon} />
                <Flex position={'absolute'} w='100%' h='100%' alignItems='center' justifyContent='center'>
                  <Image loading={loading} src={cdn(quest.icon, { w: '36px', h: '36px' })} width="36px" height="36px" objectFit={'contain'} />
                </Flex>
              </Flex>
            ) : (
              <Image loading={loading} src={cdn(quest.icon, { w: '36px', h: '36px' })} width="36px" height="36px" objectFit={'contain'} />
            )}
          </Flex>
        </Flex>

        <Flex p='16px' mt='36px' gap='4px'>
          {/* Status badge */}

          {claimed || completed && (
            <Flex p={'2px 10px'} bgColor='#e4f6ed' borderRadius={'2px'}>
              <Text textTransform={'uppercase'} color={'#00A650'} fontSize={'14px'} fontWeight='700'>
                {t("Quests.card.completed")}
              </Text>
            </Flex>
          )}

          {/* DependsOn */}
          {dependsOnFilteredStatus && (quest.status == QUEST_STATUS.PENDING || disabled) &&
            (manual || oneClick || secretWord) && loggedIn && (
              <Flex p={'2px 10px'} bgColor='#efefef' borderRadius={'2px'}>
                <Text textTransform={'uppercase'} color={'#5C5C5C'} fontSize={'14px'} fontWeight='700'>
                  {t('Quests.status.blocked')}
                </Text>
              </Flex>
            )}

          {/* XP badge */}
          <XpPill showCheck={claimed} score={quest.score} v2 />
        </Flex>

        {/* Title, desc, button */}
        <Flex px='16px' pb='16px' flexDirection={'column'} gap='4px' flex='1'>
          <Text color='#0C0324' fontSize={'16px'} fontWeight='700'>{title()}</Text>
          <Text color='#0C0324' fontSize={'14px'} fontWeight='400'>{description}</Text>

          <Flex mt='auto' flexDirection={'column'}>
            <>

              {(dependsOnFilteredStatus && (pending || disabled)) && loggedIn && (
                <Flex borderRadius={'6px'} mb='10px'
                  sx={{
                    border: '1px solid #0000',
                    background: 'linear-gradient(90deg, #0C0324 0%, #0C0324 100%) padding-box, linear-gradient(90deg, #F8AC33 0%, #912FD4 100%) border-box',
                  }}
                  position='relative'
                >
                  <Text
                    w={'full'}
                    p='4px 8px'
                    color="#0C0324"
                    fontSize="14px"
                    fontWeight="400"
                    lineHeight={'normal'}
                    textAlign="left"
                    minHeight={'40px'}
                    background='white'
                    borderRadius={'4px'}
                  >
                    {t("Quests.card.firstMustComplete")} <br />
                    <Text onClick={() => scrollToDependsOn(parent)} cursor='pointer' as='strong' textDecoration={'underline'}>{parent?.title}</Text>
                  </Text>
                </Flex>
              )}

              {/* Buttons / web3 */}

              <>
                {quest.action && !blockchainNeeded &&
                  (pending || disabled) &&
                  (automatic || (adminCompletion)) && !dependsOnFilteredStatus && (
                    <Button
                      mt='8px'
                      variant={'filledGradient'}
                      w={'full'}
                      color={'white'}
                      fontSize="14px"
                      onClick={() => {
                        const props = {
                          quest_id: quest.id,
                          quest_name: quest.title,
                          quest_xp: quest.score,
                          user_agent: navigator.userAgent,
                          ...(fcb && { fbc: fcb }),
                          ...(fbp && { fbp: fbp }),
                        }
                        analytics()
                          ?.track('Quest Started', props)
                        goToUrl()
                      }}
                      disabled={disabled && loggedIn}
                    >
                      {buttonText}
                    </Button>
                  )}

                {/* DependsOn */}
                {quest.action && dependsOnFilteredStatus && !blockchainNeeded &&
                  (automatic || adminCompletion) && (
                    <Button
                      mt='8px'
                      variant={'filledGradient'}
                      w={'full'}
                      color={'white'}
                      fontSize="14px"
                      disabled
                    >
                      {buttonText}
                    </Button>
                  )}

                {!dependsOnFilteredStatus && (pending || disabled) &&
                  (manual || oneClick || secretWord || twitter) && !blockchainNeeded && (
                    <Button
                      mt='8px'
                      variant={'filledGradient'}
                      w={'full'}
                      color={'white'}
                      fontSize="14px"
                      onClick={handleCompleteQuest}
                      disabled={verifying || (disabled && loggedIn)}
                    >
                      {verifying ? (
                        <Spinner speed="0.85s" />
                      ) : (
                        (oneClick || secretWord || twitter) ? buttonText : t("Quests.card.verify")
                      )}
                    </Button>
                  )}

                {/* WEB3 */}
                {!dependsOnFilteredStatus && (pending || disabled) && manual && blockchainNeeded && (
                  <ThirdwebProvider clientId={import.meta.env.VITE_THIRDWEB_CLIENT_ID}>
                    <ThirdwebProviderV5>
                      <QuestWeb3Button
                        quest={quest}
                        reloadQuests={reloadQuests}
                        disabled={disabled}
                        loggedIn={loggedIn}
                        openOnboarding={openOnboarding}
                      />
                    </ThirdwebProviderV5>
                  </ThirdwebProvider>
                )}

                {/* WEB3 DependsOn not loggedIn */}
                {dependsOnFilteredStatus && (pending || disabled) && manual && blockchainNeeded && !loggedIn && (
                  <Button
                    mt='8px'
                    variant={'filledGradient'}
                    w={'full'}
                    color={'white'}
                    fontSize="14px"
                    gap='4px'
                    sx={{
                      '&:disabled': {
                        opacity: 1
                      }
                    }}
                    onClick={() => openOnboarding()}
                  >
                    {t('ConnectWallet.ConnectButtons.mainButton.label')}
                  </Button>
                )}

                {/* DependsOn */}
                {dependsOnFilteredStatus && (quest.status == QUEST_STATUS.PENDING || disabled) &&
                  (manual || oneClick || secretWord || blockchainNeeded) && loggedIn && (
                    <Button
                      mt='8px'
                      bgColor={'#718096'}
                      w={'full'}
                      color={'white'}
                      fontSize="14px"
                      disabled
                      gap='4px'
                      sx={{
                        '&:disabled': {
                          opacity: 1
                        }
                      }}
                    >
                      <Image src={IconLocked} />
                      {t('Quests.status.blocked')}
                    </Button>
                  )}

                {exhausted && (
                  <Button
                    mt='8px'
                    variant={'filledGradient'}
                    w={'full'}
                    color={'white'}
                    fontSize="14px"
                    height='40px'
                    disabled
                  >
                    {t("Quests.card.attemptsExhausted")}
                  </Button>
                )}

                {(claimed && !quest?.recursive) &&
                  <Button
                    mt='8px'
                    bgColor={'#718096'}
                    w={'full'}
                    color={'white'}
                    fontSize="14px"
                    gap='4px'
                    sx={{
                      '&:hover': {
                        backgroundColor: '#718096',
                        cursor: 'default'
                      }
                    }}
                  >
                    <Image src={IconCheck} />
                    {t("Quests.card.completed")}
                  </Button>
                }

                {/* Recursive claimed available in */}
                {(claimed && quest?.recursive) && (
                  <Flex
                    mt='8px'
                    color='#AB17DF'
                    border='1px solid #AB17DF'
                    borderRadius={'6px'}
                    alignItems='center'
                    justifyContent={'center'}
                    h='40px'
                    flexDirection={'row'}
                    gap='4px'
                  >
                    <Image src={IconClock} />
                    <Text fontWeight={'400'}>{t("Quests.status.availableIn")}</Text>
                    <Text fontWeight={'700'}><CountdownToTomorrow /></Text>
                  </Flex>
                )}

                {/* Web3 Verifyng */}
                {(verifyingWeb3 && blockchainNeeded) && (
                  <Button
                    mt='8px'
                    variant={'filledGradient'}
                    w={'full'}
                    color={'white'}
                    fontSize="14px"
                    disabled={true}
                  >
                    <Flex gap='8px' alignItems={'center'}>
                      <Spinner speed="0.85s" size={'xs'} />
                      {t('Quests.web3.verifying')}
                    </Flex>
                  </Button>
                )}

                {(completed) &&
                  <Button
                    mt='8px'
                    variant={'filledGradient'}
                    w={'full'}
                    color={'white'}
                    fontSize="14px"
                    onClick={handleClaim}
                    className={questStyles.glowClaim}
                    isLoading={verifying}
                  >
                    {t("Quests.card.getReward")}
                  </Button>
                }
              </>
            </>
          </Flex>
        </Flex>
      </Flex>

      <ModalSecretWord quest={quest} isOpen={isOpenModal} setIsOpen={setIsOpenModal} onSubmit={onSubmitSecretWord} />
      <ModalTwitterQuest quest={quest} isOpen={isOpenModalTwitter} onClose={() => setIsOpenModalTwitter(false)} />
    </>
  )
}

export default QuestCard
